import { useEffect, useState } from 'react';
import { Button, Card, Field, Input, PageHeader, Spinner, Textarea } from '../components/ui';
import { SystemAPI } from '../services/endpoints/system';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { FeatureFlags } from '../types/api';
import './common.css';

export function SettingsPage() {
  const [collection, setCollection] = useState('');
  const [names, setNames] = useState('');
  const [loading, setLoading] = useState(false);
  const { run, busy } = useDispatchAction();

  async function load() {
    if (!collection.trim()) return;
    setLoading(true);
    try {
      const data = await SystemAPI.getOduNames({ collection: collection.trim() });
      setNames(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
    } catch {
      setNames('');
    } finally { setLoading(false); }
  }

  async function save() {
    if (!collection.trim() || !names.trim()) return;
    let payload: unknown = names;
    try { payload = JSON.parse(names); } catch { /* send as string */ }
    await run('Save Odu names', () => SystemAPI.saveOduNames({ collection: collection.trim(), payload }));
  }

  // ── Feature flags ──────────────────────────────────────────────────────
  const [flags, setFlags] = useState<FeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);

  useEffect(() => {
    SystemAPI.getFeatureFlags()
      .then(setFlags)
      .catch(() => setFlags({ creatorStudioEnabled: false, videoEnabled: false, synopsisEnabled: false, trainerEnabled: false }))
      .finally(() => setFlagsLoading(false));
  }, []);

  async function saveFlags() {
    if (!flags) return;
    const saved = await run('Save feature flags', () => SystemAPI.saveFeatureFlags(flags));
    if (saved) setFlags(saved);
  }

  return (
    <div>
      <PageHeader
        eyebrow="12 — SETTINGS"
        title={<>System <em>settings</em>.</>}
        lede="Edit per-collection Odu names. Load a collection by name, edit the JSON, then save. Changes invalidate the dashboard cache automatically downstream."
      />

      <Card style={{ marginBottom: 24 }}>
        <div className="nb-form-col">
          <div className="nb-field__label">Feature flags</div>
          <div className="nb-field__hint" style={{ marginBottom: 12 }}>Toggle app features. Takes effect on the next app dashboard load.</div>
          {flagsLoading || !flags ? <Spinner /> : (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={flags.creatorStudioEnabled}
                  onChange={e => setFlags({ ...flags, creatorStudioEnabled: e.target.checked })}
                />
                <span>Creator studio <span style={{ color: 'var(--ink-3)' }}>— shows the Create tab to creators</span></span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={flags.classesEnabled ?? flags.videoEnabled}
                  // One control gates the Classes tab; keep the legacy videoEnabled in sync
                  // so older app builds (which read videoEnabled) stay aligned.
                  onChange={e => setFlags({ ...flags, classesEnabled: e.target.checked, videoEnabled: e.target.checked })}
                />
                <span>Classes <span style={{ color: 'var(--ink-3)' }}>— shows the Classes tab to all users</span></span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={flags.synopsisEnabled}
                  onChange={e => setFlags({ ...flags, synopsisEnabled: e.target.checked })}
                />
                <span>Synopsis <span style={{ color: 'var(--ink-3)' }}>— shows the Synopsis button on the media player</span></span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={flags.trainerEnabled ?? false}
                  onChange={e => setFlags({ ...flags, trainerEnabled: e.target.checked })}
                />
                <span>Opele Cast Trainer <span style={{ color: 'var(--ink-3)' }}>— shows the Trainer tab to all users</span></span>
              </label>
              <div className="nb-form-row">
                <Button tone="ember" onClick={saveFlags} disabled={busy}>Save feature flags</Button>
              </div>
            </>
          )}
        </div>
      </Card>
      <Card>
        <div className="nb-form-col">
          <div className="nb-form-row">
            <Field label="Collection name">
              <Input value={collection} onChange={e => setCollection(e.target.value)} placeholder="OGBE · OYEKU · …" />
            </Field>
            <Button onClick={load} disabled={loading || !collection.trim()}>{loading ? <Spinner /> : 'Load'}</Button>
            <Button tone="ember" onClick={save} disabled={busy || !collection.trim() || !names.trim()}>Save</Button>
          </div>
          <Field label="Names (JSON)" hint="Edit as JSON. Will be sent back exactly as posted.">
            <Textarea value={names} onChange={e => setNames(e.target.value)} rows={18} className="nb-input--mono" />
          </Field>
        </div>
      </Card>
    </div>
  );
}
